import React from "react";
import { X, MapPin } from "lucide-react";
import { formatINR } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";

function Stat({ label, value, test }) {
  return (
    <div className="rounded-lg bg-slate-50 border border-slate-200 p-3" data-testid={test}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="mt-1 text-lg font-bold text-slate-900 brand-heading">{value}</div>
    </div>
  );
}

function story(r, t, tv, lang) {
  if (!r.msmes) return t("gp_story_none");
  const sec = r.top_sectors?.[0] ? tv(r.top_sectors[0].sector) : "—";
  if (lang === "hi") {
    const tail = r.applications ? `${r.applications} आवेदनों में से ${r.sanctioned} स्वीकृत हुए हैं और ${r.active_lenders} ऋणदाता सक्रिय हैं।` : "अभी तक किसी ऋणदाता के साथ आवेदन साझा नहीं हुआ है — ऋणदाता जुड़ाव का अवसर।";
    return `${r.district} में ${r.msmes} पंजीकृत MSME हैं, जिनमें से ${r.credit_ready} ऋण-तैयार हैं। कुल माँग ${formatINR(r.demand)} है, जिसमें ${sec} प्रमुख है। ${tail}`;
  }
  const tail = r.applications
    ? `${r.sanctioned} of ${r.applications} application${r.applications === 1 ? " has" : "s have"} been sanctioned, with ${r.active_lenders} lender${r.active_lenders === 1 ? "" : "s"} active.`
    : "No application has been shared with a lender yet — an opportunity for lender engagement.";
  return `${r.district} has ${r.msmes} registered MSME${r.msmes > 1 ? "s" : ""}, ${r.credit_ready} of them credit-ready. Credit demand stands at ${formatINR(r.demand)}, led by ${sec}. ${tail}`;
}

export function DistrictPanel({ row, onClose, parentOf }) {
  const { t, tv, lang } = useLang();
  if (!row) {
    return (
      <div className="rounded-lg border border-dashed border-slate-300 bg-white p-6 text-sm text-slate-500 h-full grid place-items-center text-center" data-testid="district-panel-empty">
        <div><MapPin className="w-6 h-6 mx-auto text-slate-400" /><p className="mt-2">{t("gp_pick")}</p></div>
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-5 space-y-4" data-testid="district-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">{t("gp_district")}</div>
          <h3 className="text-2xl font-bold text-slate-900 brand-heading" data-testid="district-panel-name">{row.district}</h3>
          {parentOf && <div className="text-xs text-slate-500 mt-0.5">{t("gp_within")} {parentOf}</div>}
        </div>
        <button onClick={onClose} data-testid="district-panel-close" className="text-slate-400 hover:text-slate-700" aria-label="Close"><X className="w-5 h-5" /></button>
      </div>
      <p className="text-sm text-slate-700 leading-relaxed" data-testid="district-story">{story(row, t, tv, lang)}</p>
      <div className="grid grid-cols-2 gap-2">
        <Stat label={t("g_msmes")} value={row.msmes} test="dp-msmes" />
        <Stat label={t("g_ready")} value={row.credit_ready} test="dp-ready" />
        <Stat label={t("g_demand")} value={formatINR(row.demand)} test="dp-demand" />
        <Stat label={t("g_apps")} value={row.applications} test="dp-apps" />
        <Stat label={t("g_sanct")} value={`${row.sanctioned} · ${formatINR(row.sanctioned_amount)}`} test="dp-sanct" />
        <Stat label={t("g_disb")} value={row.disbursed} test="dp-disb" />
        <Stat label={t("g_women")} value={row.women_owned} test="dp-women" />
        <Stat label={t("gp_lenders")} value={row.active_lenders} test="dp-lenders" />
      </div>
      {row.top_sectors?.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">{t("gp_sectors")}</div>
          <div className="flex flex-wrap gap-2">{row.top_sectors.map((s) => <span key={s.sector} className="rounded-full bg-orange-50 border border-orange-200 text-orange-900 px-2.5 py-1 text-xs">{tv(s.sector)} · {s.count}</span>)}</div>
        </div>
      )}
    </div>
  );
}
